/**
 * popoverItems -- data lists builders { label, value } for InfoMode popovers.
 *
 * Each builder receives raw data + the `t` function from useI18n + additional contexts,
 * and returns an array ready to be passed as the `items` prop to HoverPopoverCard.
 *
 * UI-free, JSX-free, fully testable.
 */

import {
  formatProductArea,
  formatCompartmentExposition,
  formatCompartmentInsolation,
  formatCompartmentInsolationRange,
  formatCompartmentWindowRatio,
  formatCompartmentWindowArea,
  formatCompartmentZone,
  formatSquareMeters,
} from '../../../shared/utils/productFormatters.js'

import { COMPARTMENT_ITEM_ID } from '../constants/compartmentItemIds.js'
import { EQUIPMENT_DIMENSIONS } from '../constants/equipmentDimensions.js'

// -- Compartment ---------------------------------------------------------------

/**
 * Returns the desired column span (1, 2, or 3) for a value string.
 * DataGrid will further clamp to 'full' when span >= the actual column count.
 */
function autoSpan(value) {
  const len = String(value ?? '').length
  return len > 28 ? 3 : len > 13 ? 2 : 1
}

// -- Registry ------------------------------------------------------------------

/**
 * COMPARTMENT_ITEM_REGISTRY -- declarative list of all possible compartment data fields.
 *
 * Shared between the InfoMode hover popover and the CompartmentsList expandedRow.
 * Both pass the same `settings` shape (show* flags) produced by their respective
 * panelSettings normalizers.
 *
 * Each entry:
 *   id           -- matches COMPARTMENT_ITEM_ID
 *   labelKey     -- i18n key passed to t()
 *   isAvailable  -- (settings) => boolean -- visibility gate
 *   getValue     -- (compartment, t, compartmentTypes) => string|null
 *   getSpan      -- (value) => 1|2|3 -- optional; omit for fixed span 1
 *
 * Registry order determines render order.
 */
export const COMPARTMENT_ITEM_REGISTRY = [
  {
    id: COMPARTMENT_ITEM_ID.AREA,
    labelKey: 'product.area_m2',
    isAvailable: (settings) => settings.showArea,
    getValue: (compartment) => formatProductArea(compartment),
  },
  {
    id: COMPARTMENT_ITEM_ID.EXPOSITION,
    labelKey: 'product.exposition',
    isAvailable: (settings) => settings.showExposition,
    getValue: (compartment, t) => formatCompartmentExposition(compartment, t),
    getSpan: (value) => autoSpan(value),
  },
  {
    id: COMPARTMENT_ITEM_ID.INSOLATION_HOURS,
    labelKey: 'product.insolation_hours',
    isAvailable: (settings) => settings.showInsolationHours,
    getValue: (compartment) => formatCompartmentInsolation(compartment),
  },
  {
    id: COMPARTMENT_ITEM_ID.INSOLATION_RANGE,
    labelKey: 'product.insolation_range',
    isAvailable: (settings) => settings.showInsolationRange,
    getValue: (compartment) => formatCompartmentInsolationRange(compartment),
    getSpan: (value) => autoSpan(value),
  },
  {
    id: COMPARTMENT_ITEM_ID.WINDOWS_RATIO,
    labelKey: 'product.windows_ratio',
    isAvailable: (settings) => settings.showWindowsRatio,
    getValue: (compartment) => formatCompartmentWindowRatio(compartment),
  },
  {
    id: COMPARTMENT_ITEM_ID.WINDOWS_AREA,
    labelKey: 'product.windows_area',
    isAvailable: (settings) => settings.showWindowsArea,
    getValue: (compartment) => formatCompartmentWindowArea(compartment),
  },
  {
    id: COMPARTMENT_ITEM_ID.ZONE,
    labelKey: 'product.zone',
    isAvailable: (settings) => settings.showZone,
    getValue: (compartment, t, compartmentTypes) => formatCompartmentZone(compartment, compartmentTypes, t),
  },
  // {
  //   id: COMPARTMENT_ITEM_ID.HEIGHT,
  //   labelKey: 'product.height',
  //   isAvailable: (settings) => settings.showHeight,
  //   getValue: (compartment) => ...,
  // },
  // {
  //   id: COMPARTMENT_ITEM_ID.AREA_WALLS,
  //   labelKey: 'product.area_walls',
  //   isAvailable: (settings) => settings.showAreaWalls,
  //   getValue: (compartment) => ...,
  // },
]

/**
 * buildCompartmentItems -- runs the COMPARTMENT_ITEM_REGISTRY against the given compartment.
 *
 * @param {object}      compartment
 * @param {object|null} compartmentTypes -- raw compartments.json
 * @param {function}    t
 * @param {object}      settings         -- show* visibility flags
 * @returns {Array<{label:string, value:string, span?:number}>}
 */
export function buildCompartmentItems(compartment, compartmentTypes, t, settings) {
  const out = []

  for (const entry of COMPARTMENT_ITEM_REGISTRY) {
    if (!entry.isAvailable(settings)) continue
    const value = entry.getValue(compartment, t, compartmentTypes)
    if (value == null) continue
    const span = entry.getSpan?.(value)
    out.push({ label: t(entry.labelKey), value, ...(span != null ? { span } : {}) })
  }

  return out
}

// -- Opening (window) ----------------------------------------------------------

/**
 * @param {object}   win -- one entry from product.tiers[n].windows[]
 * @param {function} t
 * @returns {Array<{label:string, value:string}>}
 */
export function buildOpeningItems(win, t) {
  const out = []

  const hours = formatCompartmentInsolation(win)
  if (hours != null) out.push({ label: t('product.insolation_hours'), value: hours })

  const range = formatCompartmentInsolationRange(win)
  if (range != null) out.push({ label: t('product.insolation_range'), value: range, span: autoSpan(range) })

  return out
}

// -- Inner walls ---------------------------------------------------------------

/**
 * @param {number|null} areaWalls
 * @param {function}    t
 * @returns {Array<{label:string, value:string}>}
 */
export function buildInnerWallsItems(areaWalls, t) {
  const value = formatSquareMeters(areaWalls)
  return value != null ? [{ label: t('product.area_walls'), value }] : []
}

// -- Equipment -----------------------------------------------------------------

const SZ_RE = /^sz_(\d+)x(\d+)$/

const formatDimension = (val) => {
  if (val === null || Number.isNaN(val)) return '?';
  return parseFloat(val.toFixed(0));
};

/**
 * Parses a raw equipment id into a display name (translated) + optional dimension string.
 *
 * sz_{W}x{D}    => { name: t('equipment.sz', id), dimensions: 'W × D cm' }
 * anything else => { name: t('equipment.{id}', id), dimensions: null }
 *
 * @param {string|null} id
 * @param {function}    t
 * @returns {{ name: string, dimensions: string|null }}
 */
export function parseEquipmentId(id, t) {
  if (!id) return { name: '', dimensions: null }
  const m = SZ_RE.exec(id)
  if (m) {
    return { name: t('equipment.sz', id), dimensions: `${m[1]} × ${m[2]} cm` }
  }
  return { name: t(`equipment.${id}`, id), dimensions: null }
}

const _getEquipmentDimensions = (id, scale) => {
  if (!id) return [null, null]
  
  const item = EQUIPMENT_DIMENSIONS.find(e => e.id === id)
  if (!item) return [null, null]
  
  const rawX = Number(item?.default_x_length);
  const rawY = Number(item?.default_y_length);

  if (Number.isNaN(rawX) || Number.isNaN(rawY)) return [null, null];

  const scaleX = scale?.x !== undefined && !Number.isNaN(Number(scale.x)) ? Number(scale.x) : 1;
  const scaleY = scale?.y !== undefined && !Number.isNaN(Number(scale.y)) ? Number(scale.y) : scaleX;

  return [
    rawX * scaleX,
    rawY * scaleY
  ];
}

/**
 * Parses a raw equipment id into a display name (translated) + optional dimension string.
 *
 * @param {string|null} id
 * @param {object}      scale
 * @param {function}    t
 * @returns {{ name: string, dimensions: string|null }}
 */
export function parseEquipmentItem({id, scale, t}) {
  if (!id) return { name: '', dimensions: null }
  const m = SZ_RE.exec(id)
  if (m) {
    return { 
      name: t('equipment.sz'), 
      dimensions: `${m[1]} × ${m[2]} cm` 
    }
  }
  const [scaledX, scaledY] = _getEquipmentDimensions(id, scale);

  if (scaledX === null || scaledY === null) {
    return { 
      name: t(`equipment.${id}`), 
      dimensions: null
    };
  }

  const displayX = formatDimension(scaledX);
  const displayY = formatDimension(scaledY);

  return { 
    name: t(`equipment.${id}`), 
    dimensions: `${displayX} × ${displayY} cm` 
  };
}
